/**
 * Canonical column / value type model and inference helpers.
 *
 * This module is the single source of truth for classifying values, inferring
 * column types, and declaring producer-owned column schemas. It consolidates
 * what previously lived in three files:
 *
 * - `type-inference.ts` — primitive value / column type inference
 * - `infer-column-types.ts` — record-array column type inference (unused; removed)
 * - `plugin-chat/data-pipeline/types.ts` — `DatasetColumnType` / `ColumnSchema`
 *
 * Four layers, ordered primitive → rich:
 *
 * 1. {@link DatasetColumnType} + {@link ColumnRefKind} — the shared vocabulary
 * 2. {@link classifyValue} — classify a single value
 * 3. {@link inferColumnType} — threshold-based dominant type over a value list
 * 4. {@link inferColumnSchemas} — all-rows strict schema inference for dataset producers
 * 5. {@link columnsFromView} — schema extraction from a CDF `DataModelView`
 */
import { isInstanceReference } from '#core/lib/cognite/types.ts';
import type { DataModelView } from '#core/lib/cognite/view.ts';
import { extractViewColumnTypes } from '#core/lib/cognite/view.ts';
import { ISO_DATE_REGEX_STRICT } from '#core/lib/utils/date.ts';

// Type vocabulary

/**
 * The set of column / value types recognised across the codebase.
 *
 * Intentionally smaller than JavaScript's runtime type space:
 *
 * - `'datetime'` captures both `Date` instances and ISO 8601 strings
 * - `'ref'` captures CDF instance references (`{ space, externalId }`)
 * - `'unknown'` is the fallback for `null` / `undefined`, arrays, plain
 *   (non-ref) objects, mixed columns, and any other value that doesn't
 *   fit cleanly into a declared type
 */
export type DatasetColumnType = 'string' | 'number' | 'boolean' | 'datetime' | 'ref' | 'unknown';

/** Semantic hint for columns of type `'ref'`. */
export type ColumnRefKind = 'entity' | 'ts' | 'unknown';

/** Schema for a single column in a producer-declared dataset. */
export interface ColumnSchema {
  /** Column key as it appears in each row record. */
  name: string;
  /** Value type carried by this column. */
  type: DatasetColumnType;
  /** Reference kind, present only when {@link type} is `'ref'`. */
  refKind?: ColumnRefKind;
  /** Optional unit hint propagated from upstream schemas (e.g. CDF views). */
  unit?: string;
  /** Human-readable description propagated from upstream schemas. */
  description?: string;
  /**
   * Whether this column was auto-synthesized by the dataset factory rather
   * than declared by the producer. Synthetic columns participate in pipeline
   * operations (filter, join, etc.) like any other column but are hidden
   * from LLM-facing column listings to avoid cluttering the model's view of
   * the schema. See `createDataset` for the synthesis rules.
   */
  synthetic?: boolean;
}

/**
 * Row record shape used by row-scanning helpers. Kept as a private alias so
 * signatures don't nest `ReadonlyArray<ValueRecord>`.
 */
type ValueRecord = Record<string, unknown>;

// Single-value classification

/**
 * Classify a single value into a {@link DatasetColumnType}.
 *
 * - `null` / `undefined` → `'unknown'`
 * - booleans → `'boolean'`
 * - numbers (finite, `NaN`, `Infinity`) → `'number'`
 * - strings matching {@link ISO_DATE_REGEX_STRICT} → `'datetime'`
 * - other strings → `'string'`
 * - `Date` instances → `'datetime'`
 * - CDF instance references (`{ space, externalId }`) → `'ref'`
 * - everything else (arrays, plain objects, symbols, bigints) → `'unknown'`
 *
 * This is the "lossy-per-value" classifier that drives {@link inferColumnType}.
 * For column-level strict semantics (where a mixed ISO / non-ISO string
 * column stays `'string'` rather than being promoted per value), use
 * {@link inferColumnSchemas}.
 */
export function classifyValue(value: unknown): DatasetColumnType {
  if (value === null || value === undefined) {
    return 'unknown';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  if (typeof value === 'number') {
    return 'number';
  }
  if (typeof value === 'string') {
    return ISO_DATE_REGEX_STRICT.test(value) ? 'datetime' : 'string';
  }
  if (value instanceof Date) {
    return 'datetime';
  }
  if (isInstanceReference(value)) {
    return 'ref';
  }
  return 'unknown';
}

// Threshold-based column type inference

/** Options for {@link inferColumnType}. */
export interface InferColumnTypeOptions {
  /**
   * Minimum fraction of non-null values that must classify to a single type
   * for that type to win. Values below the threshold return `'unknown'`.
   * Default: `0.9`.
   */
  threshold?: number;
}

/**
 * Infer the dominant column type from a list of values using a threshold.
 *
 * `null` and `undefined` are dropped before counting. If no type reaches
 * the threshold, returns `'unknown'`. When multiple types meet the
 * threshold, priority order is:
 *
 * `number` → `boolean` → `datetime` → `ref` → `string`
 *
 * This is the "fast & lossy" variant — use it when you want a best-guess
 * classification that tolerates a bit of noise. For strict schema inference
 * that rejects any mixing, use {@link inferColumnSchemas}.
 */
export function inferColumnType(values: readonly unknown[], options?: InferColumnTypeOptions): DatasetColumnType {
  const threshold = options?.threshold ?? 0.9;

  const counts: Record<DatasetColumnType, number> = {
    string: 0,
    number: 0,
    boolean: 0,
    datetime: 0,
    ref: 0,
    unknown: 0,
  };

  let total = 0;
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }
    counts[classifyValue(value)]++;
    total++;
  }

  if (total === 0) {
    return 'unknown';
  }

  // Priority order: most specific → least specific, so a column meeting the
  // threshold for multiple types resolves predictably (e.g. a column of
  // numeric-looking values resolves to 'number' rather than 'string').
  for (const type of INFER_COLUMN_TYPE_PRIORITY) {
    if (counts[type] / total >= threshold) {
      return type;
    }
  }
  return 'unknown';
}

const INFER_COLUMN_TYPE_PRIORITY: readonly DatasetColumnType[] = ['number', 'boolean', 'datetime', 'ref', 'string'];

// Strict full-scan column schema inference

/**
 * Build a {@link ColumnSchema} array by scanning every row.
 *
 * Unlike {@link inferColumnType}, this function rejects any column where
 * non-null values classify to more than one kind — such columns become
 * `'unknown'` in the output. String columns are also handled differently:
 * a column is promoted to `'datetime'` only when every non-null value
 * matches ISO 8601; a mixed ISO / non-ISO column stays `'string'`.
 *
 * Rules:
 *
 * - All values same type → that type
 * - All values null / undefined → `'unknown'`
 * - Mixed types → `'unknown'`
 * - All strings, all ISO 8601 → `'datetime'`
 * - All strings, any non-ISO → `'string'`
 * - All CDF refs → `'ref'` with `refKind: 'unknown'` (raw values can't
 *   distinguish entity from timeseries refs; use {@link columnsFromView}
 *   when you have the view schema)
 *
 * Columns are emitted in first-seen order across all rows.
 */
export function inferColumnSchemas(rows: readonly ValueRecord[]): ColumnSchema[] {
  const columnNames = collectColumnNames(rows);
  return columnNames.map((name) => buildInferredColumnSchema(name, rows));
}

function collectColumnNames(rows: readonly ValueRecord[]): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        names.push(key);
      }
    }
  }
  return names;
}

function buildInferredColumnSchema(name: string, rows: readonly ValueRecord[]): ColumnSchema {
  let observed: DatasetColumnType | undefined;
  let allStringsAreIsoDatetime = true;
  let sawValue = false;

  for (const row of rows) {
    const value = row[name];
    if (value === null || value === undefined) {
      continue;
    }
    sawValue = true;

    // Column-level strict classification differs from classifyValue:
    // strings stay 'string' here, so a mixed ISO / non-ISO column degrades
    // to 'string' instead of falling through to 'unknown'.
    const kind = strictClassifyValue(value);

    if (observed === undefined) {
      observed = kind;
    } else if (observed !== kind) {
      return { name, type: 'unknown' };
    }

    if (kind === 'string') {
      allStringsAreIsoDatetime &&= ISO_DATE_REGEX_STRICT.test(value as string);
    }
  }

  if (!sawValue || observed === undefined) {
    return { name, type: 'unknown' };
  }

  if (observed === 'string' && allStringsAreIsoDatetime) {
    return { name, type: 'datetime' };
  }

  if (observed === 'ref') {
    return { name, type: 'ref', refKind: 'unknown' };
  }

  return { name, type: observed };
}

/**
 * Like {@link classifyValue} but keeps strings as `'string'` instead of
 * promoting ISO 8601 to `'datetime'`. {@link buildInferredColumnSchema}
 * re-does the promotion at the column level so mixed ISO / non-ISO columns
 * stay `'string'` rather than falling through to `'unknown'`.
 */
function strictClassifyValue(value: unknown): DatasetColumnType {
  if (value === null || value === undefined) {
    return 'unknown';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  if (typeof value === 'number') {
    return 'number';
  }
  if (typeof value === 'string') {
    return 'string';
  }
  if (value instanceof Date) {
    return 'datetime';
  }
  if (isInstanceReference(value)) {
    return 'ref';
  }
  return 'unknown';
}

// Schema extraction from CDF views

/**
 * Extract a typed {@link ColumnSchema} array from a CDF `DataModelView`.
 *
 * Uses Zod schema introspection (via {@link extractViewColumnTypes}) to
 * determine column types authoritatively, without scanning runtime data.
 *
 * When `properties` is supplied, output columns follow that list's order
 * and any name not present in the view is silently skipped. When omitted,
 * every property exposed by {@link extractViewColumnTypes} is emitted in
 * introspection order.
 *
 * Returns `[]` for an undefined view to match the ergonomics of
 * {@link extractViewColumnTypes}.
 */
export function columnsFromView(view: DataModelView | undefined, properties?: readonly string[]): ColumnSchema[] {
  const viewColumns = extractViewColumnTypes(view);
  if (!viewColumns) {
    return [];
  }

  const names = properties ?? [...viewColumns.keys()];

  const result: ColumnSchema[] = [];
  for (const name of names) {
    const descriptor = viewColumns.get(name);
    if (!descriptor) {
      continue;
    }
    result.push(buildViewColumnSchema(name, descriptor));
  }
  return result;
}

/** Shape of a single entry returned by {@link extractViewColumnTypes}. */
interface ViewColumnDescriptor {
  type: string;
  referencedSchema?: string;
  /** Raw (unwrapped) Zod field schema — carried for type introspection downstream. */
  schema?: unknown;
  /**
   * Human-readable description captured from the original (pre-unwrap) field
   * schema. Populated by `extractViewColumnTypes` from the raw view shape so
   * descriptions on wrapped types (ZodOptional, ZodNullable) survive
   * `deepRemoveDefaults`.
   */
  description?: string;
}

function buildViewColumnSchema(name: string, descriptor: ViewColumnDescriptor): ColumnSchema {
  const description = descriptor.description;
  const descriptionField = description ? { description } : {};
  const type = viewColumnTypeToCanonical(descriptor.type);
  if (type === 'ref') {
    return {
      name,
      type,
      refKind: deriveRefKind(descriptor.referencedSchema),
      ...descriptionField,
    };
  }
  return {
    name,
    type,
    ...descriptionField,
  };
}

function deriveRefKind(referencedSchema: string | undefined): ColumnRefKind {
  if (referencedSchema === 'TimeSeries') {
    return 'ts';
  }
  if (referencedSchema) {
    return 'entity';
  }
  return 'unknown';
}

/**
 * Mapping from Zod v4 type tags (as returned by {@link extractViewColumnTypes})
 * to the canonical {@link DatasetColumnType}. Data-driven to keep the mapping
 * auditable in one place and to avoid a long-match switch statement.
 */
const ZOD_TYPE_TO_COLUMN_TYPE: Readonly<Record<string, DatasetColumnType>> = {
  string: 'string',
  enum: 'string',
  literal: 'string',
  number: 'number',
  int: 'number',
  float: 'number',
  bigint: 'number',
  boolean: 'boolean',
  date: 'datetime',
};

/**
 * Map the raw type tag from {@link extractViewColumnTypes} — a Zod v4 type tag
 * or the synthetic `'instanceId'` marker — to the canonical
 * {@link DatasetColumnType}.
 *
 * Single source of truth for "view column type → canonical column type",
 * shared by column schema inference ({@link buildViewColumnSchema}) and
 * filter-time type compatibility checks in downstream packages.
 */
export function viewColumnTypeToCanonical(type: string): DatasetColumnType {
  if (type === 'instanceId') {
    return 'ref';
  }
  return ZOD_TYPE_TO_COLUMN_TYPE[type] ?? 'unknown';
}
